/**
 * Master-key (MK) identity lifecycle — the TS half. Extracted from
 * wisermail-web/src/services/crypto.ts (wisermail#117 key hierarchy).
 *
 * MK is a STABLE random 32-byte key, decoupled from the password. It is wrapped
 * under KEK = deriveWkUser(password) and stored server-side as
 * `users.wrapped_master_key`. Password change only re-wraps MK, so everything
 * keyed off MK survives untouched. This module provides the pure crypto; session
 * state / account-key derivation stays in the consuming app.
 */

import { deriveWkUser } from './kdf';

/** Wrapped MK blob length: nonce(12) ‖ AES-256-GCM(MK=32) ‖ tag(16). */
export const WRAPPED_MASTER_KEY_LEN = 60;
const MK_WRAP_NONCE_LEN = 12;

/** Derive the password wrapping key (KEK) from password + per-user salt. */
export async function deriveKEK(password: string, salt: Uint8Array): Promise<Uint8Array> {
  return deriveWkUser(password, salt);
}

/** Generate a fresh random 32-byte Master Key (new accounts). */
export function generateMasterKey(): Uint8Array {
  return crypto.getRandomValues(new Uint8Array(32));
}

/** Wrap a 32-byte MK under a KEK with AES-256-GCM → nonce(12) ‖ ct(32) ‖ tag(16). */
export async function wrapMasterKey(mk: Uint8Array, kek: Uint8Array): Promise<Uint8Array> {
  if (mk.byteLength !== 32) throw new Error(`MK must be 32 bytes; got ${mk.byteLength}`);
  if (kek.byteLength !== 32) throw new Error(`KEK must be 32 bytes; got ${kek.byteLength}`);
  const key = await crypto.subtle.importKey('raw', kek as BufferSource, { name: 'AES-GCM' }, false, ['encrypt']);
  const nonce = crypto.getRandomValues(new Uint8Array(MK_WRAP_NONCE_LEN));
  const ctWithTag = new Uint8Array(
    await crypto.subtle.encrypt({ name: 'AES-GCM', iv: nonce as BufferSource }, key, mk as BufferSource),
  );
  const out = new Uint8Array(WRAPPED_MASTER_KEY_LEN);
  out.set(nonce, 0);
  out.set(ctWithTag, MK_WRAP_NONCE_LEN);
  return out;
}

/** Unwrap a 60-byte wrapped MK with a KEK. Throws on wrong KEK / corrupt blob. */
export async function unwrapMasterKey(wrapped: Uint8Array, kek: Uint8Array): Promise<Uint8Array> {
  if (wrapped.byteLength !== WRAPPED_MASTER_KEY_LEN) {
    throw new Error(`wrapped MK must be ${WRAPPED_MASTER_KEY_LEN} bytes; got ${wrapped.byteLength}`);
  }
  if (kek.byteLength !== 32) throw new Error(`KEK must be 32 bytes; got ${kek.byteLength}`);
  const key = await crypto.subtle.importKey('raw', kek as BufferSource, { name: 'AES-GCM' }, false, ['decrypt']);
  const nonce = wrapped.slice(0, MK_WRAP_NONCE_LEN);
  const ctWithTag = wrapped.slice(MK_WRAP_NONCE_LEN);
  const mk = new Uint8Array(
    await crypto.subtle.decrypt({ name: 'AES-GCM', iv: nonce as BufferSource }, key, ctWithTag as BufferSource),
  );
  if (mk.byteLength !== 32) throw new Error(`unwrap produced ${mk.byteLength} bytes; expected 32`);
  return mk;
}
